import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Badge from '@repo/ui/badge'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'

import { checkAAKeyword } from '../../../../api/aa-keywords/check-aa-keyword'
import { getCseByCode } from '../../../../api/cse/get-cse-by-code'
import { getCseRegistration } from '../../../../api/enrollments/add-cse-registration'
import useTranslate from '../../../../lib/hooks/use-translate'
import { GenderEnum, genderOptionsMapping } from '../../../../lib/types/enums/common/gender.enum'
import {
  ContactEnum,
  contactEnumMapping
} from '../../../../lib/types/enums/enrollment/contact.enum'
import { EnrollmentInput } from '../../../../lib/types/models/enrollment/enrollment-input'
import ControlledSwitch from '../../../elements/controlled-switch'
import { FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledPhoneInput from '../../../elements/inputs/controlled-phone-input'
import ControlledRadioInput from '../../../elements/inputs/controlled-radio-input'
import { ParentInformationInput } from '../../enrollment-profile/shared/parent-information-form'

type Props = {
  parentNumber: '1' | '2'
  required?: boolean
  priorityContactRequired?: boolean
  disabled?: boolean
}

const ParentInformation = ({
  parentNumber,
  required,
  priorityContactRequired,
  disabled
}: Props) => {
  const form = useFormContext<EnrollmentInput>()
  const profession = form.watch(`parent${parentNumber}Job`)
  const genderError = form.formState.errors[`parent${parentNumber}Gender`]?.message

  const { data: isPossibleAA } = useQuery({
    queryKey: ['check-aa-keyword', profession],
    queryFn: () => {
      if (profession) return checkAAKeyword({ label: profession })
      return false
    },
    enabled: !!profession
  })

  return (
    <div className="flex flex-col gap-2">
      <Badge text={`Parent ${parentNumber}`} variant="warning" />
      <div className="flex gap-10">
        {Object.values(GenderEnum).map((gender) => (
          <FormField
            disabled={disabled}
            key={gender}
            control={form.control}
            name={`parent${parentNumber}Gender`}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledRadioInput
                    {...field}
                    label={genderOptionsMapping[gender].title}
                    checked={field.value === gender}
                    onCheck={() => field.onChange(gender)}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        ))}
      </div>
      {genderError && (
        <Typography variant="small" color={tailwindColors.red.DEFAULT}>
          {String(genderError)}
        </Typography>
      )}
      <div className="flex-1 flex gap-2">
        <FormField
          disabled={disabled}
          control={form.control}
          name={`parent${parentNumber}LastName`}
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledInput
                  {...field}
                  required={required || priorityContactRequired}
                  label={'NOM'}
                  type="text"
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          disabled={disabled}
          control={form.control}
          name={`parent${parentNumber}FirstName`}
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledInput
                  {...field}
                  required={required || priorityContactRequired}
                  label={'Prénom'}
                  type="text"
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>

      <div className="flex-1 flex gap-2">
        <FormField
          disabled={disabled}
          control={form.control}
          name={`parent${parentNumber}PhoneNumber`}
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledPhoneInput
                  {...field}
                  required={required || priorityContactRequired}
                  label="N° de portable"
                  defaultCountry="FR"
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          disabled={disabled}
          control={form.control}
          name={`parent${parentNumber}Email`}
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledInput
                  type="email"
                  required={required || priorityContactRequired}
                  {...field}
                  label="Email"
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>

      <div className="flex-1 flex gap-2">
        <FormField
          disabled={disabled}
          control={form.control}
          name={`parent${parentNumber}Job`}
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledInput
                  type="text"
                  {...field}
                  label="Profession"
                  className={cn({ 'text-primary': isPossibleAA })}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          disabled={disabled}
          control={form.control}
          name={`parent${parentNumber}PhoneNumber2`}
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledPhoneInput {...field} label="Tél travail" defaultCountry="FR" />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    </div>
  )
}

type ParentsInformationEnrollmentFormProps = {
  enrollmentId?: number
  // CSE code chosen in the stay of an enrollment being created (no CSE registration yet)
  cseCode?: string | null
}

const ParentsInformationEnrollmentForm = ({
  enrollmentId,
  cseCode
}: ParentsInformationEnrollmentFormProps) => {
  const form = useFormContext<ParentInformationInput>()
  const { t } = useTranslate()
  const contactPriority = form.watch('contactPriority')

  const handleContactPriorityChange = (value: ContactEnum) => {
    form.setValue('contactPriority', value, {
      shouldDirty: true,
      shouldTouch: true
    })

    const fields = [
      'parent2Gender',
      'parent2FirstName',
      'parent2LastName',
      'parent2PhoneNumber',
      'parent2Email'
    ] as const

    if (value === ContactEnum.PARENT_1) {
      form.clearErrors(fields)
      return
    }

    const parent2 = form.getValues()
    const requiredFields = [
      {
        name: 'parent2Gender',
        value: parent2.parent2Gender,
        message: t('errors.enum.required', { fieldName: 'Genre' })
      },
      {
        name: 'parent2FirstName',
        value: parent2.parent2FirstName,
        message: t('errors.string.required', { fieldName: 'Prénom' })
      },
      {
        name: 'parent2LastName',
        value: parent2.parent2LastName,
        message: t('errors.string.required', { fieldName: 'NOM' })
      },
      {
        name: 'parent2PhoneNumber',
        value: parent2.parent2PhoneNumber,
        message: t('errors.phone.required')
      },
      {
        name: 'parent2Email',
        value: parent2.parent2Email,
        message: t('errors.string.required', { fieldName: 'Email' })
      }
    ] as const

    for (const field of requiredFields) {
      form.setValue(field.name, field.value, { shouldTouch: true })
      if (!field.value?.trim())
        form.setError(field.name, { type: 'required', message: field.message })
    }
  }

  const { data: cseRegistration } = useQuery({
    queryKey: ['cse-registration', enrollmentId],
    queryFn: () => {
      if (enrollmentId) return getCseRegistration(enrollmentId)
    },
    enabled: !!enrollmentId
  })

  const { data: cseByCode } = useQuery({
    queryKey: ['cse', cseCode, 'BASIC'],
    queryFn: () => {
      if (cseCode) return getCseByCode(cseCode, 'BASIC')
    },
    enabled: !enrollmentId && !!cseCode
  })

  const cse = enrollmentId ? cseRegistration?.cse : cseCode ? cseByCode : undefined

  return (
    <div className="flex flex-col gap-4">
      {cse?.extraOptionEnabled && (
        <div className="px-2 py-1">
          <Typography variant="label-semibold">Champs CSE</Typography>

          <div className="h-4" />

          <FormField
            control={form.control}
            name="extraOptionLabel"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} label={cse.extraOptionLabel} type="text" />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      )}
      <ParentInformation parentNumber="1" required />
      <ParentInformation
        parentNumber="2"
        priorityContactRequired={contactPriority === ContactEnum.PARENT_2}
      />
      <div className="px-2 py-1">
        <Typography variant="label-semibold">Contact prioritaire :</Typography>
        <div className="h-2" />
        <div className="flex gap-10">
          <FormField
            control={form.control}
            name="contactPriority"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledRadioInput
                    {...field}
                    label={contactEnumMapping[ContactEnum.PARENT_1]}
                    checked={field.value === ContactEnum.PARENT_1}
                    onCheck={() => handleContactPriorityChange(ContactEnum.PARENT_1)}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="contactPriority"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledRadioInput
                    {...field}
                    label={contactEnumMapping[ContactEnum.PARENT_2]}
                    checked={field.value === ContactEnum.PARENT_2}
                    onCheck={() => handleContactPriorityChange(ContactEnum.PARENT_2)}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>

      <FormField
        control={form.control}
        name="isAA"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormControl>
              <ControlledSwitch
                {...field}
                label="Taguer AA"
                variant="tag"
                withBorder
                borderClassName="border-grey-light px-3 py-2 rounded"
              />
            </FormControl>
          </FormItem>
        )}
      />
    </div>
  )
}

export default ParentsInformationEnrollmentForm
